import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import URI from 'urijs'

const SPLIT_CHARS = /[\s+,;?!]/

const COMMON_WORDS = [
  'about after again against all an another any and are as at',
  'be being been before but by',
  'can could',
  "did do don't down",
  'each',
  'few from for',
  'get got',
  'had has have he her here his him himself hers how',
  "if i'm in into is it it's its",
  'just',
  'like lot',
  'made me more most my',
  'no not',
  'of off on once only or other our out over own',
  'said she should so some such',
  'than that the their them then there these they this those through to too',
  'under until up',
  'very',
  "was wasn't we were we're what when where which while who why will with would wouldn't",
  'you your'
]
  .join(' ')
  .split(' ')

function resultToWords (result) {
  let words = (result.get('value') ?? '')
    .toLowerCase()
    .split(SPLIT_CHARS)
  words = words.map(word => word.replace(/[~_]/g, ' '))
  words = _.reject(words, function (word) {
    // allow single digit numbers
    const notNumeric = word.match(/\d/) === null
    return (word.length === 1 && notNumeric) || word.length === 0
  })
  return words
}

function previousModerationState (result) {
  switch (result.previous('approved')) {
    case true:
      return 'approved'
    case false:
      return 'rejected'
    default:
      return 'pending'
  }
}

function increment (wordState, words = []) {
  changeWordStat.call(this, wordState, 1, words)
}

function decrement (wordState, words = []) {
  changeWordStat.call(this, wordState, -1, words)
}

function changeWordStat (wordState, step, words = []) {
  const tmp = _.clone(this.get(wordState))
  words.forEach((term) => {
    if (!Number.isInteger(tmp[term])) {
      tmp[term] = 0
    }
    tmp[term] += step
    if (tmp[term] < 1) {
      return delete tmp[term]
    }
  })
  this.set(wordState, tmp)
}

function calculateUnmoderatedFrequency () {
  let value
  const tmpFrequency = {}

  const object = this.get('approved')
  for (var key in object) {
    value = object[key]
    if (!Number.isInteger(tmpFrequency[key])) {
      tmpFrequency[key] = 0
    }
    tmpFrequency[key] += value
  }

  const object1 = this.get('pending')
  for (key in object1) {
    value = object1[key]
    if (!Number.isInteger(tmpFrequency[key])) {
      tmpFrequency[key] = 0
    }
    tmpFrequency[key] += value
  }

  this.set({ frequency: tmpFrequency })
}

function createList () {
  const cleansed = _.omit(this.get('frequency'), COMMON_WORDS)

  // _.each(object) fails sometimes so do manual lookup
  // instead of delegating to the object iterator
  // TODO: figure out how to render emoji in wordcloud
  // emojis render properly in SVG but voting doesn't seem to parse them properly
  return _.reduce(
    _.keys(cleansed),
    function (memo, term) {
      const occurrence = cleansed[term]
      memo.push({ text: term, frequency: occurrence })
      return memo
    },
    []
  )
}

function truncateList (list, maxWordsDisplayed) {
  // break ties by the newest words
  const shortList = _.sortBy(list.reverse(), word => -word.frequency)
  if (shortList.length > maxWordsDisplayed) {
    return shortList.slice(0, +(maxWordsDisplayed - 1) + 1 || undefined)
  } else {
    return shortList
  }
}

function addResult (result) {
  increment.call(this, result.moderationState(), resultToWords(result))
  this.updateFrequency()
}

function updateResult (result) {
  decrement.call(this, previousModerationState(result), resultToWords(result))
  addResult.call(this, result)
}

function removeResult (result) {
  decrement.call(this, result.moderationState(), resultToWords(result))
  this.updateFrequency()
}

export default class WordCloudStats extends Backbone.Model {
  preinitialize (attributes) {
    const { poll } = attributes
    delete attributes.poll

    this.poll = poll
    this.flashOptions = this.poll.flashOptions()
  }

  initialize () {
    if (this.poll && this.poll.type() === 'free_text_poll') {
      this.bindEvents()
    }
  }

  bindEvents () {
    this.listenTo(this.flashOptions, 'change:teamPollArchiveId', (_, archiveId) => {
      this.set('archive_id', archiveId)
    })

    this.listenTo(this.poll.results, 'add', addResult)
    this.listenTo(this.poll.results, 'change:approved', updateResult)
    this.listenTo(this.poll.results, 'remove', removeResult)
    this.listenTo(this.poll.results, 'reset', this.clear)

    this.listenTo(this.poll, 'change:moderation_enabled', () => this.fetch())
    this.listenTo(this.poll.results, 'change:moderation_enabled', () => this.fetch())
  }

  updateFrequency () {
    if (this.poll.get('moderation_enabled')) {
      this.set({ frequency: _.clone(this.get('approved')) })
    } else {
      calculateUnmoderatedFrequency.call(this)
    }
  }

  fetch (options = {}) {
    if (!this.poll) return Backbone.$.Deferred().resolve()
    if (this.poll.type() !== 'free_text_poll') return Backbone.$.Deferred().resolve()
    if (!this.flashOptions.has('chart_type')) return Backbone.$.Deferred().resolve()
    if (this.flashOptions.get('chart_type') !== 'wordcloud') return Backbone.$.Deferred().resolve()

    if (this.has('archive_id')) {
      options.data = {
        ...(options.data ?? {}),
        archive_id: this.get('archive_id')
      }
    }

    const request = super.fetch(options)
    request.then(this.updateFrequency.bind(this))

    return request
  }

  url () {
    return URI(this.poll.url()).segment('words').toString()
  }

  defaults () {
    return {
      frequency: {},
      approved: {},
      rejected: {},
      pending: {}
    }
  }

  clear () {
    if (this.poll && this.poll.results && this.poll.results.length === 0) {
      this.set(this.defaults())
      this.set('frequency', {})
    }
  }

  createShortList (wordsOnScreen = 0) {
    if (!Number.isInteger(wordsOnScreen)) {
      wordsOnScreen = 0
    }
    const list = createList.call(this)
    const maxWordsDisplayed = wordsOnScreen > 0 ? wordsOnScreen + 10 : 100

    return truncateList.call(this, list, maxWordsDisplayed)
  }
}
